/**
 * Convierte el error de una llamada al API en un texto que se pueda pintar.
 *
 * Por que hace falta
 * ------------------
 * FastAPI devuelve dos formas distintas en `detail`:
 *
 *   400/404/500 ->  "Solo se pueden editar facturas en borrador"   (texto)
 *   422         ->  [{type, loc, msg, input, ctx, url}, ...]       (lista de objetos)
 *
 * El codigo hacia `toast.error(error.response.data.detail)` para las dos. Con
 * un 422 eso mete un OBJETO dentro de un componente de React, React no sabe
 * pintarlo y revienta con el error #31 -- y lo que ve la persona no es un
 * aviso: es la PANTALLA EN BLANCO.
 *
 * Paso de verdad el 1-sep-2026 al cambiar la forma de pago de una factura, con
 * el sistema ya en produccion. El aviso decia exactamente lo que estaba mal
 * ("due_date: input is too short") y nadie pudo leerlo.
 */

/** Nombres de campo en cristiano, para que el aviso sirva de algo. */
const NOMBRES = {
  due_date: 'Fecha de vencimiento',
  start_date: 'Fecha de inicio',
  end_date: 'Fecha de fin',
  payment_method: 'Forma de pago',
  customer_id: 'Cliente',
  items: 'Líneas',
  line_items: 'Líneas',
  total: 'Total',
  subtotal: 'Base imponible',
  platform_email: 'Email de plataforma',
  invoice_reference: 'Referencia',
  billing_email: 'Email de facturas',
  email: 'Email',
  name: 'Nombre',
  fiscal_name: 'Nombre fiscal',
  tax_id: 'NIF/CIF',
  phone: 'Teléfono',
  postal_code: 'Código postal',
  province: 'Provincia',
  sepa_iban: 'IBAN',
};

function nombreDeCampo(loc) {
  if (!Array.isArray(loc)) return '';
  // loc viene como ['body', 'due_date'] o ['body', 'items', 0, 'quantity'].
  // 'body' no le dice nada a nadie, se quita.
  const partes = loc.filter((p) => p !== 'body');
  if (partes.length === 0) return '';
  const campo = String(partes[0]);
  return NOMBRES[campo] || campo;
}

/**
 * @param {unknown} error        el error capturado (normalmente de axios)
 * @param {string} porDefecto    que decir si no se puede sacar nada mejor
 * @returns {string} siempre un string, nunca un objeto
 */
export function mensajeDeError(error, porDefecto = 'Ha ocurrido un error') {
  const detail = error?.response?.data?.detail;

  if (typeof detail === 'string' && detail.trim()) return detail;

  // 422: lista de errores de validacion
  if (Array.isArray(detail) && detail.length > 0) {
    const lineas = detail.slice(0, 4).map((e) => {
      const campo = nombreDeCampo(e?.loc);
      const msg = e?.msg || 'valor no válido';
      return campo ? `${campo}: ${msg}` : msg;
    });
    const resto = detail.length > 4 ? ` (y ${detail.length - 4} más)` : '';
    return `Hay datos que el servidor no acepta — ${lineas.join(' · ')}${resto}`;
  }

  // Algunas respuestas traen un objeto suelto en detail
  if (detail && typeof detail === 'object') {
    return detail.msg || detail.message || porDefecto;
  }

  if (typeof error?.message === 'string' && error.message.trim()) {
    return error.message;
  }

  return porDefecto;
}

/**
 * Deja `detail` convertido en texto ANTES de que llegue a la pantalla.
 *
 * Por que aqui y no en cada catch
 * -------------------------------
 * Esta funcion la llama el interceptor de axios (App.js), una sola vez, para
 * todas las llamadas de la aplicacion.
 *
 * Hay 111 sitios que hacen `toast.error(error.response.data.detail || '...')`.
 * Cada uno es una pantalla en blanco esperando: basta con que el servidor
 * conteste un 422 -- una fecha mal, un email que no le gusta -- para que ahi
 * viaje un OBJETO, React no sepa pintarlo y se lleve la aplicacion por
 * delante. Arreglar los 111 no sirve: el 112 que se escriba manana vuelve a
 * estar mal.
 *
 * Es el mismo razonamiento que con los None del PDF: se normaliza en el
 * origen, no en los cuarenta sitios que leen.
 *
 * Volvio a pasar el 7-sep-2026 guardando dos emails en la ficha de BLITZ FOOD:
 * el aviso decia exactamente que estaba mal y lo que se vio fue una pantalla
 * en blanco con "Minified React error #31" en la consola.
 *
 * Se guarda la lista original en `detail_original` por si alguien la necesita.
 */
export function aplanarDetalle(error) {
  const data = error?.response?.data;
  // Puede no haber respuesta (se cayo la red) o no ser un objeto (un Blob de
  // una descarga, un texto suelto): entonces no hay nada que aplanar.
  if (!data || typeof data !== 'object') return error;
  if (typeof data.detail === 'undefined' || typeof data.detail === 'string') return error;

  try {
    const texto = mensajeDeError(error, 'El servidor no ha aceptado los datos');
    data.detail_original = data.detail;
    data.detail = texto;
  } catch (e) {
    // Un aviso que no se puede construir no puede tumbar la peticion.
    data.detail = 'El servidor no ha aceptado los datos';
  }
  return error;
}

export default mensajeDeError;
